import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "./ScrollEffects";
import { WordReveal } from "./TextEffects";
import { PhoneFrame } from "./ui/PhoneFrame";

const checklistVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
};

const checkItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] } },
};

const TypingDots: React.FC = () => (
  <motion.div
    className="flex justify-start"
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true }}
    transition={{ delay: 1.2, duration: 0.4 }}
  >
    <div className="chat-bubble-pet max-w-[80px] flex items-center gap-1 py-3">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-2 h-2 rounded-full bg-muted-foreground/40"
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 0.6, delay: i * 0.15, repeat: Infinity, repeatDelay: 0.6, ease: "easeInOut" }}
        />
      ))}
    </div>
  </motion.div>
);

export const ChatDemoSection: React.FC = () => {
  const { t } = useTranslation();

  const messages = [
    { role: "user", text: t('chat_demo.messages.user_1') },
    { role: "pet", text: t('chat_demo.messages.pet_1') },
    { role: "user", text: t('chat_demo.messages.user_2') },
    { role: "pet", text: t('chat_demo.messages.pet_2') },
  ];

  const checklist = t('chat_demo.checklist', { returnObjects: true }) as string[];

  return (
    <section
      className="py-16 sm:py-20 px-4 relative overflow-hidden"
      style={{ background: "hsl(30 50% 96% / 0.5)" }}
    >
      <div className="max-w-5xl mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
        <ScrollReveal direction="left" className="flex-1">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-4">
            {t('chat_demo.section_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-4 leading-tight">
            <WordReveal
              text={`${t('chat_demo.section_title_prefix')} ${t('chat_demo.section_title_highlight')}`}
              highlightWords={t('chat_demo.section_title_highlight').split(' ')}
            />
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-8">
            {t('chat_demo.section_subtitle')}
          </p>
          <motion.ul
            className="space-y-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            variants={checklistVariants}
          >
            {Array.isArray(checklist) && checklist.map((item, i) => (
              <motion.li key={i} className="flex items-start gap-3 text-foreground" variants={checkItemVariants}>
                <motion.span
                  className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                  whileHover={{ scale: 1.2 }}
                >
                  ✓
                </motion.span>
                <span className="text-sm font-medium leading-relaxed">{item}</span>
              </motion.li>
            ))}
          </motion.ul>
        </ScrollReveal>

        <ScrollReveal direction="right" className="flex-1 w-full flex justify-center">
          <PhoneFrame className="max-w-[320px] lg:max-w-[340px] w-full">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-border bg-background/80 backdrop-blur-sm">
              <motion.span
                className="text-xl"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                🐕
              </motion.span>
              <div>
                <p className="font-bold text-sm text-foreground">{t('chat_demo.pet_name')}</p>
                <div className="flex items-center gap-1.5">
                  <motion.span
                    className="w-1.5 h-1.5 rounded-full bg-primary"
                    animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  <p className="text-[10px] text-primary font-medium">{t('chat_demo.pet_status')}</p>
                </div>
              </div>
            </div>

            <div className="p-3 space-y-2.5 min-h-[240px] bg-background">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.2, duration: 0.5, type: "spring", stiffness: 300, damping: 25 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={m.role === "user" ? "chat-bubble-user max-w-[80%]" : "chat-bubble-pet max-w-[80%]"}>
                    <p className="text-sm leading-relaxed">{m.text}</p>
                  </div>
                </motion.div>
              ))}
              <TypingDots />
            </div>

            <div className="px-3 py-2.5 border-t border-border bg-background">
              <div className="flex items-center gap-2 bg-muted rounded-2xl px-4 py-2.5">
                <span className="text-muted-foreground/50 text-sm">{t('chat_demo.input_placeholder')}</span>
              </div>
            </div>
          </PhoneFrame>
        </ScrollReveal>
      </div>
    </section>
  );
};
